import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { JournalEntry } from '../../../common/model/dto/journal-entry/journal-entry';
import {
  WearableDataMetricKey,
  WearableDataUtils,
} from '../../../common/utils/services/wearable-data-utils';
import OvWearableScoreRing from './OvWearableScoreRing';
import OvProviderSourceBadge from './OvProviderSourceBadge';
import { useTranslation } from 'react-i18next';

interface OvWearableDataSummaryProps {
  journalEntry?: JournalEntry;
  compact?: boolean;
  variant?: 'light' | 'dark';
  className?: string;
}

const OvWearableDataSummary: FC<OvWearableDataSummaryProps> = ({
  journalEntry,
  compact = false,
  variant = 'light',
  className,
}) => {
  const { t } = useTranslation();
  const totalSleepDuration = WearableDataUtils.getWearableMetric(
    journalEntry,
    WearableDataMetricKey.TotalSleepDuration
  );
  const sleepScore = WearableDataUtils.getWearableMetric(
    journalEntry,
    WearableDataMetricKey.SleepScore
  );
  const readinessScore = WearableDataUtils.getWearableMetric(
    journalEntry,
    WearableDataMetricKey.ReadinessScore
  );

  const sleepScoreValue = WearableDataUtils.getNumberValue(sleepScore?.value);
  const readinessScoreValue = WearableDataUtils.getNumberValue(
    readinessScore?.value
  );
  const hasKnownMetrics =
    !!totalSleepDuration ||
    sleepScoreValue !== undefined ||
    readinessScoreValue !== undefined;

  if (!hasKnownMetrics) {
    return null;
  }

  const provider = WearableDataUtils.getSharedProvider([
    totalSleepDuration,
    sleepScoreValue !== undefined ? sleepScore : undefined,
    readinessScoreValue !== undefined ? readinessScore : undefined,
  ]);

  return (
    <Container className={className} $compact={compact} $variant={variant}>
      {provider ? (
        <SummaryHeader>
          <OvProviderSourceBadge provider={provider} compact={compact} />
        </SummaryHeader>
      ) : null}

      {totalSleepDuration ? (
        <MetricRow>
          <MetricLabel $variant={variant}>
            {t('common.wearable.totalSleep')}
          </MetricLabel>
          <MetricValue $variant={variant}>
            {WearableDataUtils.formatSleepDurationSeconds(
              totalSleepDuration.value
            )}
          </MetricValue>
        </MetricRow>
      ) : null}

      {(sleepScoreValue !== undefined || readinessScoreValue !== undefined) && (
        <ScoreGrid>
          {sleepScoreValue !== undefined ? (
            <OvWearableScoreRing
              score={sleepScoreValue}
              label={t('common.wearable.sleepScore')}
              compact={compact}
              variant={variant}
            />
          ) : null}
          {readinessScoreValue !== undefined ? (
            <OvWearableScoreRing
              score={readinessScoreValue}
              label={t('common.wearable.readinessScore')}
              compact={compact}
              variant={variant}
            />
          ) : null}
        </ScoreGrid>
      )}
    </Container>
  );
};

export default OvWearableDataSummary;

const Container = styled.section<{
  $compact: boolean;
  $variant: 'light' | 'dark';
}>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? '0.5rem' : '0.75rem')};
  width: 100%;
  padding: ${(props) => (props.$compact ? '0.625rem 0' : '0.75rem 0')};
  border-top: 0;
  border-bottom: 0;
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_SEMI_LIGHT : Colours.OV_BASE};
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const MetricLabel = styled.span<{ $variant: 'light' | 'dark' }>`
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_GRAY : Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const MetricValue = styled.span<{ $variant: 'light' | 'dark' }>`
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_SEMI_LIGHT : Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  text-align: right;
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
  gap: 0.5rem 0.75rem;
`;
